/**
 * CompanyList.js
 * List all companies with search, filter, deactivate/reactivate, and delete functionality
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { Modal, DataTable } from '../components/SharedComponents';
import { API_BASE } from '../config';
import colors from '../utils/adminColors';

function CompanyList() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState(null);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Test company cleanup state
  const [testCompanies, setTestCompanies] = useState([]);
  const [testCompanyChecked, setTestCompanyChecked] = useState({});
  const [showTestCleanup, setShowTestCleanup] = useState(false);
  const [findingTestCompanies, setFindingTestCompanies] = useState(false);
  const [deletingTestCompanies, setDeletingTestCompanies] = useState(false);
  const [showTestDeleteConfirm, setShowTestDeleteConfirm] = useState(false);

  const [newCompany, setNewCompany] = useState({
    company_name: '',
    company_code: '',
    industry: '',
    contact_email: '',
    contact_phone: '',
    description: '',
  });

  useEffect(() => {
    fetchCompanies();
  }, [token, showInactive]);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const fetchCompanies = async () => {
    try {
      const url = showInactive
        ? `${API_BASE}/api/admin/companies?include_inactive=true`
        : `${API_BASE}/api/admin/companies`;
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (e, company) => {
    e.stopPropagation();
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/${company.company_id}/deactivate`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: data.message || `Company deactivated` });
        fetchCompanies();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to deactivate company' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to deactivate company: ' + error.message });
    }
  };

  const handleReactivate = async (e, company) => {
    e.stopPropagation();
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/${company.company_id}/reactivate`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: data.message || `Company reactivated` });
        fetchCompanies();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to reactivate company' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to reactivate company: ' + error.message });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || deleteConfirmText !== deleteTarget.company_name) return;
    setDeleting(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/${deleteTarget.company_id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: data.message || 'Company permanently deleted' });
        setDeleteTarget(null);
        setDeleteConfirmText('');
        fetchCompanies();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to delete company' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete company: ' + error.message });
    } finally {
      setDeleting(false);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();

    if (!newCompany.company_name || !newCompany.company_code) {
      setMessage({ type: 'error', text: 'Company name and code are required' });
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newCompany),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: `Company "${newCompany.company_name}" created successfully!` });
        setShowCreateModal(false);
        resetForm();
        fetchCompanies();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to create company' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to create company: ' + error.message });
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setNewCompany({
      company_name: '',
      company_code: '',
      industry: '',
      contact_email: '',
      contact_phone: '',
      description: '',
    });
  };

  const handleFindTestCompanies = async () => {
    setFindingTestCompanies(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/test-companies`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (response.ok) {
        const found = data.companies || [];
        setTestCompanies(found);
        const checked = {};
        found.forEach(c => { checked[c.company_id] = true; });
        setTestCompanyChecked(checked);
        setShowTestCleanup(true);
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to find test companies' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to find test companies: ' + error.message });
    } finally {
      setFindingTestCompanies(false);
    }
  };

  const handleDeleteTestCompanies = async () => {
    const selectedIds = Object.entries(testCompanyChecked)
      .filter(([, checked]) => checked)
      .map(([id]) => parseInt(id));
    if (selectedIds.length === 0) return;

    setDeletingTestCompanies(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/batch-delete-test`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ company_ids: selectedIds }),
      });
      const data = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: `Deleted ${data.count} test ${data.count === 1 ? 'company' : 'companies'}` });
        setShowTestCleanup(false);
        setShowTestDeleteConfirm(false);
        setTestCompanies([]);
        setTestCompanyChecked({});
        fetchCompanies();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to delete test companies' });
        setShowTestDeleteConfirm(false);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete test companies: ' + error.message });
      setShowTestDeleteConfirm(false);
    } finally {
      setDeletingTestCompanies(false);
    }
  };

  const selectedTestCount = Object.values(testCompanyChecked).filter(Boolean).length;

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = !searchQuery ||
      company.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.company_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.industry?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = !statusFilter || company.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status, isActive) => {
    if (isActive === false) {
      return (
        <span style={{
          padding: '4px 10px',
          borderRadius: '20px',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-semibold)',
          backgroundColor: `${colors.textMuted}15`,
          color: colors.textMuted,
        }}>
          Inactive
        </span>
      );
    }
    const styles = {
      active: { bg: `${colors.success}15`, color: colors.success },
      inactive: { bg: `${colors.warning}15`, color: colors.warning },
      deleted: { bg: `${colors.danger}15`, color: colors.danger },
    };
    const style = styles[status] || styles.inactive;

    return (
      <span style={{
        padding: '4px 10px',
        borderRadius: '20px',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-semibold)',
        backgroundColor: style.bg,
        color: style.color,
        textTransform: 'capitalize',
      }}>
        {status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div style={{
        padding: 'var(--space-page-padding)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: colors.lightGray,
      }}>
        <div style={{ textAlign: 'center' }}>

          <div style={{ color: colors.textMuted }}>Loading companies...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 'var(--space-page-padding)', backgroundColor: colors.lightGray, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '24px',
      }}>
        <div>
          <h1 style={{
            fontSize: 'var(--font-size-hero)',
            fontWeight: 'var(--font-weight-bold)',
            color: colors.darkText,
            marginBottom: '8px',
          }}>
            Companies
          </h1>
          <p style={{ color: colors.textMuted, fontSize: 'var(--font-size-base)' }}>
            Manage tenant organizations on the platform
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 18px',
            backgroundColor: colors.accent,
            color: colors.white,
            border: 'none',
            borderRadius: '8px',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semibold)',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Company
        </button>
      </div>

      {/* Message Toast */}
      {message && (
        <div style={{
          marginBottom: '16px',
          padding: '12px 16px',
          borderRadius: '8px',
          backgroundColor: message.type === 'success' ? `${colors.success}15` : `${colors.danger}15`,
          borderLeft: `4px solid ${message.type === 'success' ? colors.success : colors.danger}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span style={{ color: message.type === 'success' ? colors.success : colors.danger }}>
            {message.text}
          </span>
          <button
            onClick={() => setMessage(null)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 'var(--font-size-md)',
              color: colors.textMuted,
            }}
          >
            x
          </button>
        </div>
      )}

      {/* Filters */}
      <div style={{
        backgroundColor: colors.white,
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '24px',
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
        boxShadow: 'var(--shadow-sm)',
      }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <span style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: 'var(--font-size-md)',
          }}>

          </span>
          <input
            type="text"
            placeholder="Search companies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              border: `1px solid ${colors.mediumGray}`,
              borderRadius: '8px',
              fontSize: 'var(--font-size-base)',
              outline: 'none',
            }}
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '10px 16px',
            border: `1px solid ${colors.mediumGray}`,
            borderRadius: '8px',
            fontSize: 'var(--font-size-base)',
            outline: 'none',
            backgroundColor: colors.white,
            minWidth: '150px',
          }}
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="deleted">Deleted</option>
        </select>

        {/* Show Inactive Toggle */}
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          cursor: 'pointer',
          fontSize: 'var(--font-size-sm)',
          color: colors.textMuted,
          whiteSpace: 'nowrap',
          userSelect: 'none',
        }}>
          <div
            onClick={() => setShowInactive(!showInactive)}
            style={{
              width: '36px',
              height: '20px',
              borderRadius: '10px',
              backgroundColor: showInactive ? colors.adminPurple : colors.mediumGray,
              position: 'relative',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
          >
            <div style={{
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              backgroundColor: colors.white,
              position: 'absolute',
              top: '2px',
              left: showInactive ? '18px' : '2px',
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
          </div>
          Show inactive
        </label>

        <button
          onClick={handleFindTestCompanies}
          disabled={findingTestCompanies}
          style={{
            padding: '10px 16px',
            backgroundColor: 'transparent',
            border: `1px solid ${colors.warning}80`,
            borderRadius: '8px',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: colors.warning,
            cursor: findingTestCompanies ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap',
            opacity: findingTestCompanies ? 0.6 : 1,
          }}
        >
          {findingTestCompanies ? 'Searching...' : 'Find Test Companies'}
        </button>

        {(searchQuery || statusFilter) && (
          <button
            onClick={() => { setSearchQuery(''); setStatusFilter(''); }}
            style={{
              padding: '10px 16px',
              backgroundColor: 'transparent',
              border: `1px solid ${colors.mediumGray}`,
              borderRadius: '8px',
              fontSize: 'var(--font-size-base)',
              color: colors.textMuted,
              cursor: 'pointer',
            }}
          >
            Clear
          </button>
        )}
      </div>

      {/* Results count */}
      <div style={{
        marginBottom: '16px',
        fontSize: 'var(--font-size-base)',
        color: colors.textMuted
      }}>
        {filteredCompanies.length} {filteredCompanies.length === 1 ? 'company' : 'companies'}
        {(searchQuery || statusFilter) && ` matching filters`}
      </div>

      {/* Companies List */}
      {filteredCompanies.length === 0 ? (
        <div style={{
          backgroundColor: colors.white,
          borderRadius: '12px',
          padding: '60px 20px',
          textAlign: 'center',
          boxShadow: 'var(--shadow-sm)',
        }}>

          <h3 style={{ fontSize: 'var(--font-size-md)', color: colors.darkText, marginBottom: '8px' }}>
            {searchQuery || statusFilter ? 'No companies found' : 'No companies yet'}
          </h3>
          <p style={{ color: colors.textMuted, marginBottom: '24px' }}>
            {searchQuery || statusFilter
              ? 'Try adjusting your search or filters'
              : 'Create your first company to get started'}
          </p>
          {!searchQuery && !statusFilter && (
            <button
              onClick={() => setShowCreateModal(true)}
              style={{
                padding: '12px 24px',
                backgroundColor: colors.accent,
                color: colors.white,
                border: 'none',
                borderRadius: '8px',
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-semibold)',
                cursor: 'pointer',
              }}
            >
              Create Company
            </button>
          )}
        </div>
      ) : (
        <div style={{
          backgroundColor: colors.white,
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-sm)',
        }}>
          <DataTable
            columns={[
              { key: 'company_name', header: 'Company', render: (val, row) => (
                <span style={{ opacity: row.is_active === false ? 0.5 : 1 }}>
                  <span style={{ fontWeight: 'var(--font-weight-semibold)', color: colors.accent, cursor: 'pointer' }}>{row.company_name}</span>
                  <span style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, marginLeft: '8px' }}>{row.company_code}</span>
                </span>
              )},
              { key: 'industry', header: 'Industry', render: (val, row) => (
                <span style={{ color: colors.textMuted, opacity: row.is_active === false ? 0.5 : 1 }}>{val || '-'}</span>
              )},
              { key: 'user_count', header: 'Users', align: 'center', render: (val, row) => (
                <span style={{ fontWeight: 'var(--font-weight-semibold)', color: row.user_count > 0 ? colors.darkText : colors.textMuted, opacity: row.is_active === false ? 0.5 : 1 }}>{val || 0}</span>
              )},
              { key: 'material_count', header: 'Materials', align: 'center', render: (val, row) => (
                <span style={{ fontWeight: 'var(--font-weight-semibold)', color: val > 0 ? colors.darkText : colors.textMuted, opacity: row.is_active === false ? 0.5 : 1 }}>{val || 0}</span>
              )},
              { key: 'supplier_count', header: 'Suppliers', align: 'center', render: (val, row) => (
                <span style={{ fontWeight: 'var(--font-weight-semibold)', color: val > 0 ? colors.darkText : colors.textMuted, opacity: row.is_active === false ? 0.5 : 1 }}>{val || 0}</span>
              )},
              { key: 'created_at', header: 'Created', render: (val, row) => (
                <span style={{ color: colors.textMuted, opacity: row.is_active === false ? 0.5 : 1 }}>{formatDate(val)}</span>
              )},
              { key: 'status', header: 'Status', align: 'center', render: (val, row) => getStatusBadge(val, row.is_active) },
              { key: 'company_id', header: '', align: 'right', render: (val, row) => (
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' }}>
                  {row.is_active === false ? (
                    <>
                      <button
                        onClick={(e) => handleReactivate(e, row)}
                        style={{ padding: '4px 10px', backgroundColor: 'transparent', border: `1px solid ${colors.success}50`, borderRadius: '5px', fontSize: 'var(--font-size-xs)', color: colors.success, cursor: 'pointer', fontWeight: 'var(--font-weight-medium)', lineHeight: 1.4 }}
                      >Reactivate</button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(row); setDeleteConfirmText(''); }}
                        style={{ padding: '4px 10px', backgroundColor: 'transparent', border: 'none', fontSize: 'var(--font-size-xs)', color: colors.danger, cursor: 'pointer', fontWeight: 'var(--font-weight-medium)', lineHeight: 1.4, opacity: 0.8 }}
                      >Delete</button>
                    </>
                  ) : (
                    <button
                      onClick={(e) => handleDeactivate(e, row)}
                      style={{ padding: '4px 10px', backgroundColor: 'transparent', border: `1px solid ${colors.mediumGray}`, borderRadius: '5px', fontSize: 'var(--font-size-xs)', color: colors.textMuted, cursor: 'pointer', fontWeight: 'var(--font-weight-medium)', lineHeight: 1.4 }}
                    >Deactivate</button>
                  )}
                </div>
              )},
            ]}
            data={filteredCompanies}
            onRowClick={(row) => navigate(`/admin/companies/${row.company_id}`)}
          />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => { setDeleteTarget(null); setDeleteConfirmText(''); }}
        title="Delete Company Permanently"
        size="medium"
      >
        {deleteTarget && (
          <div>
            <div style={{
              padding: '16px',
              backgroundColor: `${colors.danger}08`,
              border: `1px solid ${colors.danger}30`,
              borderRadius: '8px',
              marginBottom: '20px',
            }}>
              <p style={{ color: colors.danger, fontWeight: 'var(--font-weight-semibold)', marginBottom: '8px', fontSize: 'var(--font-size-base)' }}>
                This will permanently delete {deleteTarget.company_name} and all associated data. This cannot be undone.
              </p>
              <p style={{ color: colors.textMuted, fontSize: 'var(--font-size-sm)' }}>
                All users, materials, suppliers, RFPs, bids, contracts, knowledge documents, and other data belonging to this company will be permanently removed.
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-base)' }}>
                Type the company name to confirm:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={deleteTarget.company_name}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: `1px solid ${deleteConfirmText === deleteTarget.company_name ? colors.danger : colors.mediumGray}`,
                  borderRadius: '8px',
                  fontSize: 'var(--font-size-base)',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'transparent',
                  border: `1px solid ${colors.mediumGray}`,
                  borderRadius: '8px',
                  fontSize: 'var(--font-size-base)',
                  color: colors.darkText,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteConfirmText !== deleteTarget.company_name || deleting}
                style={{
                  padding: '10px 20px',
                  backgroundColor: deleteConfirmText === deleteTarget.company_name ? colors.danger : colors.mediumGray,
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: colors.white,
                  cursor: deleteConfirmText === deleteTarget.company_name && !deleting ? 'pointer' : 'not-allowed',
                  opacity: deleteConfirmText === deleteTarget.company_name ? 1 : 0.5,
                }}
              >
                {deleting ? 'Deleting...' : 'Delete permanently'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Create Company Modal */}
      <Modal isOpen={showCreateModal} onClose={() => { setShowCreateModal(false); resetForm(); }} title="Create New Company" size="medium">
            <form onSubmit={handleCreateCompany}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-base)' }}>
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={newCompany.company_name}
                    onChange={(e) => setNewCompany({ ...newCompany, company_name: e.target.value })}
                    placeholder="e.g., Acme Corporation"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${colors.mediumGray}`,
                      borderRadius: '8px',
                      fontSize: 'var(--font-size-base)',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-base)' }}>
                    Company Code *
                  </label>
                  <input
                    type="text"
                    value={newCompany.company_code}
                    onChange={(e) => setNewCompany({ ...newCompany, company_code: e.target.value.toUpperCase() })}
                    placeholder="e.g., ACME"
                    maxLength={20}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${colors.mediumGray}`,
                      borderRadius: '8px',
                      fontSize: 'var(--font-size-base)',
                      outline: 'none',
                      boxSizing: 'border-box',
                      textTransform: 'uppercase',
                    }}
                  />
                  <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, marginTop: '4px' }}>
                    Unique identifier for this company (letters, numbers, hyphens)
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-base)' }}>
                    Industry
                  </label>
                  <input
                    type="text"
                    value={newCompany.industry}
                    onChange={(e) => setNewCompany({ ...newCompany, industry: e.target.value })}
                    placeholder="e.g., Manufacturing, Aerospace, Technology"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${colors.mediumGray}`,
                      borderRadius: '8px',
                      fontSize: 'var(--font-size-base)',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-base)' }}>
                      Contact Email
                    </label>
                    <input
                      type="email"
                      value={newCompany.contact_email}
                      onChange={(e) => setNewCompany({ ...newCompany, contact_email: e.target.value })}
                      placeholder="admin@company.com"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: `1px solid ${colors.mediumGray}`,
                        borderRadius: '8px',
                        fontSize: 'var(--font-size-base)',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-base)' }}>
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      value={newCompany.contact_phone}
                      onChange={(e) => setNewCompany({ ...newCompany, contact_phone: e.target.value })}
                      placeholder="+1 (555) 123-4567"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: `1px solid ${colors.mediumGray}`,
                        borderRadius: '8px',
                        fontSize: 'var(--font-size-base)',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-base)' }}>
                    Description
                  </label>
                  <textarea
                    value={newCompany.description}
                    onChange={(e) => setNewCompany({ ...newCompany, description: e.target.value })}
                    placeholder="Brief description of the company..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${colors.mediumGray}`,
                      borderRadius: '8px',
                      fontSize: 'var(--font-size-base)',
                      outline: 'none',
                      boxSizing: 'border-box',
                      resize: 'vertical',
                    }}
                  />
                </div>

              {/* Modal Footer */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 'var(--space-card-gap)',
                marginTop: '20px',
              }}>
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: 'transparent',
                    border: `1px solid ${colors.mediumGray}`,
                    borderRadius: '8px',
                    fontSize: 'var(--font-size-base)',
                    color: colors.darkText,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: colors.accent,
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: colors.white,
                    cursor: creating ? 'not-allowed' : 'pointer',
                    opacity: creating ? 0.7 : 1,
                  }}
                >
                  {creating ? 'Creating...' : 'Create Company'}
                </button>
              </div>
            </form>
      </Modal>

      {/* Test Company Cleanup Modal */}
      <Modal
        isOpen={showTestCleanup}
        onClose={() => { setShowTestCleanup(false); setTestCompanies([]); setTestCompanyChecked({}); }}
        title="Test Company Cleanup"
        size="large"
      >
        {testCompanies.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', color: colors.textMuted }}>
            No test companies found
          </div>
        ) : (
          <div>
            <div style={{
              padding: '12px 16px',
              backgroundColor: `${colors.warning}10`,
              borderLeft: `4px solid ${colors.warning}`,
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: 'var(--font-size-sm)',
              color: colors.darkText,
            }}>
              Found {testCompanies.length} test {testCompanies.length === 1 ? 'company' : 'companies'} (prefixed with _p22_, _sectest_, or _dltest_)
            </div>

            <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '20px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${colors.mediumGray}` }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={selectedTestCount === testCompanies.length}
                          onChange={(e) => {
                            const newChecked = {};
                            testCompanies.forEach(c => { newChecked[c.company_id] = e.target.checked; });
                            setTestCompanyChecked(newChecked);
                          }}
                        />
                        Select All
                      </label>
                    </th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Company Name</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Users</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Status</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {testCompanies.map(c => (
                    <tr key={c.company_id} style={{ borderBottom: `1px solid ${colors.mediumGray}40` }}>
                      <td style={{ padding: '8px 12px' }}>
                        <input
                          type="checkbox"
                          checked={!!testCompanyChecked[c.company_id]}
                          onChange={(e) => setTestCompanyChecked(prev => ({ ...prev, [c.company_id]: e.target.checked }))}
                        />
                      </td>
                      <td style={{ padding: '8px 12px', fontSize: 'var(--font-size-sm)', color: colors.darkText }}>{c.company_name}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center', fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>{c.user_count || 0}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: 'var(--font-weight-medium)',
                          backgroundColor: c.is_active === false ? `${colors.textMuted}15` : `${colors.success}15`,
                          color: c.is_active === false ? colors.textMuted : colors.success,
                        }}>
                          {c.is_active === false ? 'Inactive' : 'Active'}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px', fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
                        {c.created_at ? new Date(c.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
                {selectedTestCount} of {testCompanies.length} selected
              </span>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => { setShowTestCleanup(false); setTestCompanies([]); setTestCompanyChecked({}); }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: 'transparent',
                    border: `1px solid ${colors.mediumGray}`,
                    borderRadius: '8px',
                    fontSize: 'var(--font-size-base)',
                    color: colors.darkText,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => setShowTestDeleteConfirm(true)}
                  disabled={selectedTestCount === 0}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: selectedTestCount > 0 ? colors.danger : colors.mediumGray,
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: colors.white,
                    cursor: selectedTestCount > 0 ? 'pointer' : 'not-allowed',
                    opacity: selectedTestCount > 0 ? 1 : 0.5,
                  }}
                >
                  Delete Selected ({selectedTestCount})
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Test Delete Confirmation Modal */}
      <Modal
        isOpen={showTestDeleteConfirm}
        onClose={() => setShowTestDeleteConfirm(false)}
        title="Confirm Deletion"
        size="medium"
      >
        <div>
          <div style={{
            padding: '16px',
            backgroundColor: `${colors.danger}08`,
            border: `1px solid ${colors.danger}30`,
            borderRadius: '8px',
            marginBottom: '20px',
          }}>
            <p style={{ color: colors.danger, fontWeight: 'var(--font-weight-semibold)', marginBottom: '8px', fontSize: 'var(--font-size-base)' }}>
              Are you sure you want to permanently delete {selectedTestCount} test {selectedTestCount === 1 ? 'company' : 'companies'}? This cannot be undone.
            </p>
            <p style={{ color: colors.textMuted, fontSize: 'var(--font-size-sm)' }}>
              All associated users, materials, suppliers, RFPs, and other data will be permanently removed.
            </p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <button
              onClick={() => setShowTestDeleteConfirm(false)}
              style={{
                padding: '10px 20px',
                backgroundColor: 'transparent',
                border: `1px solid ${colors.mediumGray}`,
                borderRadius: '8px',
                fontSize: 'var(--font-size-base)',
                color: colors.darkText,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteTestCompanies}
              disabled={deletingTestCompanies}
              style={{
                padding: '10px 20px',
                backgroundColor: colors.danger,
                border: 'none',
                borderRadius: '8px',
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-semibold)',
                color: colors.white,
                cursor: deletingTestCompanies ? 'not-allowed' : 'pointer',
                opacity: deletingTestCompanies ? 0.7 : 1,
              }}
            >
              {deletingTestCompanies ? 'Deleting...' : 'Delete permanently'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default CompanyList;
